import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, X, CheckCheck, Droplets } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import ModalPortal from "@/components/ui/modal-portal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SanitaryAlertsBell() {
  const [count, setCount] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  // Rappels « résultat observé » des apports biostimulants (15 jours après l'application)
  const [reminders, setReminders] = useState([]);
  const navigate = useNavigate();

  const fetchReminders = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/carnet/reminders/biostimulant-results`);
      if (res.ok) setReminders(await res.json());
    } catch { /* silencieux */ }
  }, []);

  const fetchCount = useCallback(async () => {
    const res = await authFetch(`${API}/sanitary-alerts/me/count`);
    if (res.ok) {
      const d = await res.json();
      setCount(d.unread || 0);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    const res = await authFetch(`${API}/sanitary-alerts/me?limit=20`);
    if (res.ok) setAlerts(await res.json());
  }, []);

  useEffect(() => {
    fetchCount();
    fetchReminders();
    const iv = setInterval(fetchCount, 60_000);
    window.addEventListener("veyer:reminders-changed", fetchReminders);
    return () => { clearInterval(iv); window.removeEventListener("veyer:reminders-changed", fetchReminders); };
  }, [fetchCount, fetchReminders]);

  const openReminder = (r) => {
    setOpen(false);
    navigate(`/carnet/${encodeURIComponent(r.parcel_id)}?tab=biostimulant&bio=${encodeURIComponent(r.biostimulant_id)}`);
  };

  const dismissReminder = async (r) => {
    await authFetch(`${API}/carnet/biostimulants/${r.biostimulant_id}/results-reminder/dismiss`, { method: "POST" });
    fetchReminders();
  };

  const total = count + reminders.length;

  const openPanel = async () => {
    setOpen(true);
    fetchReminders();
    await fetchAlerts();
  };

  const markRead = async (id) => {
    await authFetch(`${API}/sanitary-alerts/${id}/read`, { method: "POST" });
    fetchCount();
    fetchAlerts();
  };

  const markAll = async () => {
    await authFetch(`${API}/sanitary-alerts/mark-all-read`, { method: "POST" });
    toast.success("Toutes les alertes marquées lues");
    fetchCount();
    fetchAlerts();
  };

  return (
    <>
      <button onClick={openPanel} className="relative p-1.5 rounded-lg hover:bg-black/5" data-testid="sanitary-bell-btn">
        <Bell className="w-4 h-4" style={{ color: count > 0 ? "#DC2626" : reminders.length ? "#D97706" : "#6B7280" }} />
        {total > 0 && (
          <span className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center ${count > 0 ? "bg-red-600" : "bg-amber-600"}`} data-testid="sanitary-bell-count">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {/* Rendu dans <body> : l'en-tête qui contient la cloche (effet de flou) enfermait le panneau */}
      {open && (
        <ModalPortal>
        <div className="fixed inset-0 z-[2000] bg-black/40 flex items-start justify-end p-2 sm:p-4" onClick={() => setOpen(false)} data-testid="sanitary-alerts-panel">
          <div className="bg-white rounded-xl w-full max-w-sm overflow-hidden flex flex-col shadow-2xl mt-14" style={{ maxHeight: "min(85vh, calc(100% - 3.5rem))" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-red-600 to-red-700 text-white">
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-90">Notifications</div>
                <div className="text-sm font-bold">Rappels et alertes sanitaires</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/20"><X className="w-4 h-4" /></button>
            </div>

            {reminders.length > 0 && (
              <div className="p-3 border-b space-y-2 max-h-[40%] overflow-y-auto" data-testid="bio-results-reminders">
                <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700">À compléter ({reminders.length})</p>
                {reminders.map((r) => (
                  <div key={r.biostimulant_id} className="p-2.5 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-2" data-testid={`reminder-${r.biostimulant_id}`}>
                    <Droplets className="w-4 h-4 shrink-0 mt-0.5 text-cyan-700" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-gray-900">
                        Notez le résultat observé de <strong>{r.product_name}</strong>, appliqué le {new Date(`${r.date}T00:00:00`).toLocaleDateString("fr-FR")}.
                      </p>
                      <p className="text-[10px] text-gray-500 truncate">{r.parcel_name}</p>
                      <div className="flex gap-3 mt-1">
                        <button onClick={() => openReminder(r)} className="text-[11px] font-semibold text-cyan-800 underline" data-testid={`reminder-open-${r.biostimulant_id}`}>Renseigner</button>
                        <button onClick={() => dismissReminder(r)} className="text-[11px] text-gray-500 hover:text-gray-800" data-testid={`reminder-dismiss-${r.biostimulant_id}`}>Ignorer</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {alerts.length > 0 && (
              <div className="px-3 py-2 border-b bg-gray-50 flex justify-end">
                <button onClick={markAll} className="text-[10px] text-gray-600 hover:text-gray-900 flex items-center gap-1" data-testid="mark-all-read-btn">
                  <CheckCheck className="w-3 h-3" /> Tout marquer comme lu
                </button>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ WebkitOverflowScrolling: "touch" }}>
              {alerts.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-6">Aucune alerte sanitaire dans votre zone</p>
              ) : alerts.map(a => (
                <div key={a.alert_id} className={`p-3 rounded-lg border ${a.read ? "bg-gray-50 border-gray-200" : "bg-red-50 border-red-200"}`} data-testid={`alert-${a.alert_id}`}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${a.read ? "text-gray-400" : "text-red-600"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase" style={{ color: a.read ? "#6B7280" : "#DC2626" }}>{a.category || "Sanitaire"}</span>
                        <span className="text-[10px] text-gray-500">~{Math.round((a.approximate_distance_m || 0) / 100) * 100} m</span>
                      </div>
                      <p className={`text-[11px] mt-1 ${a.read ? "text-gray-600" : "text-gray-900"}`}>{a.message}</p>
                      <div className="mt-1 text-[10px] text-gray-400">{new Date(a.created_at).toLocaleString("fr-FR")}</div>
                    </div>
                    {!a.read && (
                      <button onClick={() => markRead(a.alert_id)} className="text-[10px] text-red-600 hover:text-red-800 whitespace-nowrap" data-testid={`mark-read-${a.alert_id}`}>Marquer lu</button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t px-3 py-2 bg-gray-50 text-[10px] text-gray-500 text-center">
              Alertes anonymes — aucune info sur l'utilisateur signalant ni sur la parcelle exacte.
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </>
  );
}
